import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';

const EquipmentDetail = () => {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem('token');

  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [workOrders, setWorkOrders] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [maintenancePlans, setMaintenancePlans] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [serviceQr, setServiceQr] = useState(null);
  const [woLoading, setWoLoading] = useState(false);
  const [ctLoading, setCtLoading] = useState(false);
  const [mpLoading, setMpLoading] = useState(false);

  const L = {
    back: isAr ? 'رجوع' : 'Back',
    edit: isAr ? 'تعديل' : 'Edit',
    overview: isAr ? 'نظرة عامة' : 'Overview',
    qr_photos: isAr ? 'QR والصور' : 'QR & Photos',
    work_orders: isAr ? 'أوامر العمل' : 'Work Orders',
    contracts: isAr ? 'العقود' : 'Contracts',
    maintenance: isAr ? 'خطط الصيانة' : 'Maintenance Plans',
    equipment_info: isAr ? 'معلومات المعدات' : 'Equipment Info',
    name: isAr ? 'الاسم' : 'Name',
    serial: isAr ? 'الرقم التسلسلي' : 'Serial Number',
    model: isAr ? 'الموديل' : 'Model',
    manufacturer: isAr ? 'الشركة المصنعة' : 'Manufacturer',
    category: isAr ? 'الفئة' : 'Category',
    client: isAr ? 'العميل' : 'Client',
    location: isAr ? 'الموقع' : 'Location',
    status: isAr ? 'الحالة' : 'Status',
    install_date: isAr ? 'تاريخ التركيب' : 'Install Date',
    warranty: isAr ? 'الضمان' : 'Warranty Expiry',
    last_visit: isAr ? 'آخر زيارة' : 'Last Visit',
    total_visits: isAr ? 'إجمالي الزيارات' : 'Total Visits',
    description: isAr ? 'الوصف' : 'Description',
    qr_code: isAr ? 'رمز QR' : 'QR Code',
    download_qr: isAr ? 'تحميل QR' : 'Download QR',
    photos: isAr ? 'الصور' : 'Photos',
    no_photos: isAr ? 'لا توجد صور' : 'No photos',
    no_items: isAr ? 'لا توجد عناصر' : 'No items found',
    loading: isAr ? 'جاري التحميل...' : 'Loading...',
    new_wo: isAr ? 'أمر عمل جديد' : 'New Work Order',
    entitlement: isAr ? 'التغطية الحالية' : 'Current Entitlement',
    covered: isAr ? 'مغطى' : 'Covered',
    not_covered: isAr ? 'غير مغطى' : 'Not Covered',
    visits_left: isAr ? 'زيارات متبقية' : 'Visits Left',
    unlimited: isAr ? 'غير محدود' : 'Unlimited',
    wo_number: isAr ? 'رقم الأمر' : 'WO #',
    priority: isAr ? 'الأولوية' : 'Priority',
    type: isAr ? 'النوع' : 'Type',
    scheduled: isAr ? 'الموعد' : 'Scheduled',
    contract_num: isAr ? 'رقم العقد' : 'Contract #',
    contract_type: isAr ? 'نوع العقد' : 'Type',
    period: isAr ? 'الفترة' : 'Period',
    value: isAr ? 'القيمة' : 'Value',
    plan_name: isAr ? 'اسم الخطة' : 'Plan Name',
    frequency: isAr ? 'التكرار' : 'Frequency',
    next_due: isAr ? 'الموعد القادم' : 'Next Due',
    active: isAr ? 'نشط' : 'Active',
  };

  const STATUS_COLORS = { active: '#10b981', maintenance: '#f59e0b', inactive: '#6b7280', retired: '#ef4444' };
  const PRIORITY_COLORS = { critical: '#dc2626', high: '#f97316', medium: '#eab308', low: '#22c55e' };
  const WO_STATUS_COLORS = { draft: '#6b7280', open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', on_hold: '#ef4444', completed: '#10b981', closed: '#6b7280', cancelled: '#991b1b' };
  const CT_STATUS_COLORS = { draft: '#6b7280', active: '#10b981', suspended: '#f59e0b', expired: '#ef4444', cancelled: '#991b1b', renewed: '#3b82f6' };

  // Styles
  const cardSt = { backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', padding: '20px' };
  const badgeSt = (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: `${color}22`, color });
  const infoRowSt = { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' };
  const infoLabelSt = { color: '#9ca3af', fontSize: '13px' };
  const infoValueSt = { color: '#f9fafb', fontSize: '13px', fontWeight: '500' };
  const btnPrimary = { padding: '8px 18px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };
  const btnOutline = { padding: '8px 18px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' };
  const tabSt = (active) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', border: 'none', borderRadius: '8px 8px 0 0',
    backgroundColor: active ? 'rgba(26, 32, 56, 0.8)' : 'transparent', color: active ? '#667eea' : '#9ca3af',
    borderBottom: active ? '2px solid #667eea' : '2px solid transparent',
  });
  const thSt = { padding: '10px 12px', textAlign: isAr ? 'right' : 'left', fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.1)' };
  const tdSt = { padding: '10px 12px', fontSize: '13px', color: '#d1d5db', borderBottom: '1px solid rgba(255,255,255,0.06)' };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  const headers = { 'Authorization': `Bearer ${token}` };

  // Fetchers
  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/equipment/${id}`, { headers });
      const data = await res.json();
      if (data.success) setEquipment(data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id, token]);

  const fetchWorkOrders = useCallback(async () => {
    try {
      setWoLoading(true);
      const res = await fetch(`${API}/work-orders?equipment_id=${id}&limit=50`, { headers });
      const data = await res.json();
      if (data.success) setWorkOrders(data.data || []);
    } catch (err) { console.error(err); }
    finally { setWoLoading(false); }
  }, [id, token]);

  const fetchContracts = useCallback(async () => {
    try {
      setCtLoading(true);
      const res = await fetch(`${API}/equipment/${id}/contracts`, { headers });
      const data = await res.json();
      if (data.success) setContracts(data.data || []);
    } catch (err) { console.error(err); }
    finally { setCtLoading(false); }
  }, [id, token]);

  const fetchMaintenancePlans = useCallback(async () => {
    try {
      setMpLoading(true);
      const res = await fetch(`${API}/maintenance-plans?equipment_id=${id}&limit=50`, { headers });
      const data = await res.json();
      if (data.success) setMaintenancePlans(data.data || []);
    } catch (err) { console.error(err); }
    finally { setMpLoading(false); }
  }, [id, token]);

  const fetchEntitlement = useCallback(async () => {
    try {
      const res = await fetch(`${API}/equipment/${id}/entitlement-check`, { headers });
      const data = await res.json();
      if (data.success) setEntitlement(data.data);
    } catch (err) { console.error(err); }
  }, [id, token]);

  const fetchServiceQR = useCallback(async () => {
    try {
      const res = await fetch(API + '/equipment/' + id + '/service-qr', { headers });
      if (res.ok) setServiceQr(await res.json());
    } catch (e) { console.error('Service QR error:', e); }
  }, [id]);

  const fetchQR = useCallback(async () => {
    try {
      const res = await fetch(API + '/equipment/' + id + '/qr', { headers });
      const data = await res.json();
      if (data.success) setQrData(data.data);
    } catch (err) { console.error(err); }
  }, [id, token]);

  useEffect(() => { fetchEquipment(); fetchEntitlement(); fetchQR(); fetchServiceQR(); }, [fetchEquipment, fetchEntitlement, fetchQR]);

  useEffect(() => {
    if (activeTab === 'work_orders') fetchWorkOrders();
    if (activeTab === 'contracts') fetchContracts();
    if (activeTab === 'maintenance') fetchMaintenancePlans();
  }, [activeTab, fetchWorkOrders, fetchContracts, fetchMaintenancePlans]);

  if (loading || !equipment) return <Layout><div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{L.loading}</div></Layout>;

  const eq = equipment;
  const photos = eq.photos || [];
  const statusColor = STATUS_COLORS[eq.status] || '#6b7280';
  const warrantyExpired = eq.warranty_expiry && new Date(eq.warranty_expiry) < new Date();

  return (
    <Layout>
    <div style={{ padding: '24px', direction: isAr ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button style={{ ...btnOutline, marginBottom: '8px', padding: '6px 12px', fontSize: '12px' }} onClick={() => navigate('/fsm/equipment')}>← {L.back}</button>
          <h1 style={{ color: '#f9fafb', fontSize: '22px', fontWeight: '700', margin: '4px 0' }}>
            {eq.category_icon || '⚙️'} {isAr && eq.name_ar ? eq.name_ar : eq.name}
          </h1>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
            <span style={badgeSt(statusColor)}>{eq.status}</span>
            {eq.category_name && <span style={badgeSt('#6b7280')}>{isAr && eq.category_name_ar ? eq.category_name_ar : eq.category_name}</span>}
            {eq.serial_number && <span style={{ color: '#9ca3af', fontSize: '13px' }}>S/N: {eq.serial_number}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnPrimary} onClick={() => navigate(`/fsm/equipment/${id}/edit`)}>✏️ {L.edit}</button>
          <button style={{ ...btnPrimary, backgroundColor: '#10b981' }} onClick={() => navigate(`/fsm/work-orders/new?equipment_id=${id}`)}>+ {L.new_wo}</button>
        </div>
      </div>

      {/* Entitlement Banner */}
      {entitlement && (
        <div style={{
          ...cardSt, marginBottom: '16px', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
          borderLeft: isAr ? 'none' : `3px solid ${entitlement.covered ? '#10b981' : '#ef4444'}`,
          borderRight: isAr ? `3px solid ${entitlement.covered ? '#10b981' : '#ef4444'}` : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>{entitlement.covered ? '✅' : '⚠️'}</span>
            <div>
              <div style={{ color: entitlement.covered ? '#10b981' : '#ef4444', fontWeight: '600', fontSize: '14px' }}>
                {entitlement.covered ? L.covered : L.not_covered}
              </div>
              {entitlement.contract && (
                <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                  {entitlement.contract.contract_number} — {entitlement.coverage_type}
                </div>
              )}
            </div>
          </div>
          {entitlement.visits_remaining !== null && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#9ca3af', fontSize: '11px' }}>{L.visits_left}</div>
              <div style={{ color: '#f9fafb', fontSize: '18px', fontWeight: '700' }}>
                {entitlement.visits_remaining !== null ? entitlement.visits_remaining : L.unlimited}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', marginBottom: '0' }}>
        {[
          { id: 'overview', label: L.overview, icon: '📋' },
          { id: 'qr_photos', label: L.qr_photos, icon: '📷' },
          { id: 'work_orders', label: L.work_orders, icon: '🔧', count: workOrders.length },
          { id: 'contracts', label: L.contracts, icon: '📄', count: contracts.length },
          { id: 'maintenance', label: L.maintenance, icon: '🛠️', count: maintenancePlans.length },
        ].map(tab => (
          <button key={tab.id} style={tabSt(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
            {tab.icon} {tab.label}
            {tab.count > 0 && <span style={{ marginLeft: '6px', padding: '1px 7px', borderRadius: '10px', fontSize: '11px', backgroundColor: 'rgba(102,126,234,0.2)', color: '#667eea' }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ ...cardSt, borderTopLeftRadius: 0 }}>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>{L.equipment_info}</h3>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.name}</span><span style={infoValueSt}>{eq.name}</span></div>
              {eq.name_ar && <div style={infoRowSt}><span style={infoLabelSt}>{L.name} (AR)</span><span style={{ ...infoValueSt, direction: 'rtl' }}>{eq.name_ar}</span></div>}
              <div style={infoRowSt}><span style={infoLabelSt}>{L.serial}</span><span style={infoValueSt}>{eq.serial_number || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.model}</span><span style={infoValueSt}>{eq.model_number || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.manufacturer}</span><span style={infoValueSt}>{eq.manufacturer || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.category}</span><span style={infoValueSt}>{eq.category_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.status}</span><span style={badgeSt(statusColor)}>{eq.status}</span></div>
            </div>
            <div>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>{L.client} & {L.location}</h3>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.client}</span><span style={infoValueSt}>{isAr && eq.client_name_ar ? eq.client_name_ar : eq.client_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.location}</span><span style={infoValueSt}>{isAr && eq.location_name_ar ? eq.location_name_ar : eq.location_name || '-'}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.install_date}</span><span style={infoValueSt}>{formatDate(eq.installation_date)}</span></div>
              <div style={infoRowSt}>
                <span style={infoLabelSt}>{L.warranty}</span>
                <span style={{ ...infoValueSt, color: warrantyExpired ? '#ef4444' : '#10b981' }}>
                  {eq.warranty_expiry ? `${formatDate(eq.warranty_expiry)} ${warrantyExpired ? '(Expired)' : '(Active)'}` : '-'}
                </span>
              </div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.last_visit}</span><span style={infoValueSt}>{formatDate(eq.last_visit_date)}</span></div>
              <div style={infoRowSt}><span style={infoLabelSt}>{L.total_visits}</span><span style={infoValueSt}>{eq.total_visits || 0}</span></div>
              {eq.notes && (
                <div style={{ marginTop: '12px' }}>
                  <span style={infoLabelSt}>{L.description}</span>
                  <p style={{ color: '#d1d5db', fontSize: '13px', marginTop: '4px' }}>{eq.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* QR & PHOTOS TAB */}
        {activeTab === 'qr_photos' && (
          <div>
            {/* QR Code */}
            {eq.qr_code && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>📱 {L.qr_code}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ backgroundColor: '#fff', padding: '12px', borderRadius: '8px', display: 'inline-block' }}>
                    {qrData && qrData.data_url ? (
                      <img src={qrData.data_url} alt="QR Code" style={{ width: '180px', height: '180px' }} />
                    ) : (
                      <div style={{ width: '180px', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>Loading...</div>
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '4px' }}>QR Code: {eq.qr_code}</div>
                    <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '12px' }}>
                      Public URL: <a href={'/equipment/' + eq.qr_code} target="_blank" rel="noreferrer" style={{ color: '#667eea' }}>/equipment/{eq.qr_code}</a>
                    </div>
                    {qrData && qrData.data_url && (
                      <button onClick={() => {
                        const link = document.createElement('a');
                        link.href = qrData.data_url;
                        link.download = 'QR-' + eq.qr_code + '.png';
                        link.click();
                      }} style={{ ...btnPrimary, border: 'none', cursor: 'pointer' }}>
                        ⬇️ {L.download_qr}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Service QR - WhatsApp Request */}
            {eq.qr_code && (
              <div style={{ marginTop: '24px', padding: '20px', backgroundColor: 'rgba(30, 64, 175, 0.1)', borderRadius: '12px', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                <h3 style={{ color: '#60a5fa', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>📲 {isAr ? 'QR طلب خدمة' : 'Service Request QR'}</h3>
                <p style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '12px' }}>
                  {isAr ? 'يفتح واتساب مع رسالة طلب خدمة جاهزة عند المسح' : 'Opens WhatsApp with a pre-filled service request when scanned'}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  {serviceQr && serviceQr.data_url ? (
                    <img src={serviceQr.data_url} alt="Service QR" style={{ width: '180px', height: '180px', borderRadius: '8px' }} />
                  ) : (
                    <div style={{ width: '180px', height: '180px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>Loading...</div>
                  )}
                  <div>
                    {serviceQr && serviceQr.wa_url && (
                      <div style={{ color: '#9ca3af', fontSize: '11px', marginBottom: '8px', wordBreak: 'break-all' }}>{serviceQr.wa_url}</div>
                    )}
                    {serviceQr && serviceQr.data_url && (
                      <button onClick={() => {
                        const link = document.createElement('a');
                        link.href = serviceQr.data_url;
                        link.download = 'ServiceQR-' + eq.qr_code + '.png';
                        link.click();
                      }} style={{
                        padding: '8px 16px', borderRadius: '8px', border: '2px solid rgba(96, 165, 250, 0.3)',
                        backgroundColor: 'transparent', color: '#60a5fa', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                      }}>
                        📥 {isAr ? 'تحميل QR الخدمة' : 'Download Service QR'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Photos */}
            <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>📷 {L.photos}</h3>
            {photos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_photos}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                {photos.map((photo, idx) => (
                  <div key={idx} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={`/${photo.file_path || photo}`} alt={`Photo ${idx + 1}`} style={{ width: '100%', height: '140px', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* WORK ORDERS TAB */}
        {activeTab === 'work_orders' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', margin: 0 }}>🔧 {L.work_orders}</h3>
              <button style={btnPrimary} onClick={() => navigate(`/fsm/work-orders/new?equipment_id=${id}`)}>+ {L.new_wo}</button>
            </div>
            {woLoading ? <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>{L.loading}</div> :
            workOrders.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <th style={thSt}>{L.wo_number}</th><th style={thSt}>{L.type}</th><th style={thSt}>{L.priority}</th>
                    <th style={thSt}>{L.status}</th><th style={thSt}>{L.scheduled}</th>
                  </tr></thead>
                  <tbody>
                    {workOrders.map(wo => (
                      <tr key={wo.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/fsm/work-orders/${wo.id}`)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ ...tdSt, color: '#667eea', fontWeight: '600' }}>{wo.wo_number}</td>
                        <td style={tdSt}>{wo.wo_type}</td>
                        <td style={tdSt}><span style={badgeSt(PRIORITY_COLORS[wo.priority] || '#6b7280')}>{wo.priority}</span></td>
                        <td style={tdSt}><span style={badgeSt(WO_STATUS_COLORS[wo.status] || '#6b7280')}>{wo.status}</span></td>
                        <td style={tdSt}>{formatDate(wo.scheduled_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CONTRACTS TAB */}
        {activeTab === 'contracts' && (
          <div>
            <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>📄 {L.contracts}</h3>
            {ctLoading ? <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>{L.loading}</div> :
            contracts.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <th style={thSt}>{L.contract_num}</th><th style={thSt}>{L.contract_type}</th>
                    <th style={thSt}>{L.status}</th><th style={thSt}>{L.period}</th><th style={thSt}>{L.value}</th>
                  </tr></thead>
                  <tbody>
                    {contracts.map(ct => (
                      <tr key={ct.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/fsm/contracts/${ct.id}`)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ ...tdSt, color: '#667eea', fontWeight: '600' }}>{ct.contract_number}</td>
                        <td style={tdSt}>{ct.contract_type}</td>
                        <td style={tdSt}><span style={badgeSt(CT_STATUS_COLORS[ct.status] || '#6b7280')}>{ct.status}</span></td>
                        <td style={tdSt}>{formatDate(ct.start_date)} — {formatDate(ct.end_date)}</td>
                        <td style={tdSt}>{ct.contract_value ? `${Number(ct.contract_value).toLocaleString()}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* MAINTENANCE PLANS TAB */}
        {activeTab === 'maintenance' && (
          <div>
            <h3 style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>🛠️ {L.maintenance}</h3>
            {mpLoading ? <div style={{ textAlign: 'center', padding: '30px', color: '#9ca3af' }}>{L.loading}</div> :
            maintenancePlans.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>{L.no_items}</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <th style={thSt}>{L.plan_name}</th><th style={thSt}>{L.frequency}</th>
                    <th style={thSt}>{L.next_due}</th><th style={thSt}>{L.status}</th>
                  </tr></thead>
                  <tbody>
                    {maintenancePlans.map(mp => {
                      const overdue = mp.next_due_date && new Date(mp.next_due_date) < new Date();
                      return (
                        <tr key={mp.id}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <td style={{ ...tdSt, fontWeight: '500', color: '#f9fafb' }}>{isAr && mp.name_ar ? mp.name_ar : mp.name}</td>
                          <td style={tdSt}>{mp.frequency}</td>
                          <td style={{ ...tdSt, color: overdue ? '#ef4444' : '#d1d5db', fontWeight: overdue ? '600' : '400' }}>
                            {formatDate(mp.next_due_date)} {overdue ? '⚠️' : ''}
                          </td>
                          <td style={tdSt}><span style={badgeSt(mp.is_active ? '#10b981' : '#6b7280')}>{mp.is_active ? L.active : 'Paused'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default EquipmentDetail;